import React from 'react';
import { fetchUtils, Create, SimpleForm, SelectInput, DateInput, Button } from 'react-admin';
import RunReportIcon from '@material-ui/icons/PlayCircleFilledRounded';
import CloudDownload from '@material-ui/icons/CloudDownload';
import DataTable from 'react-data-table-component';
import { NoBidChartComponent } from './components/chart.js';

const fetch = fetchUtils.fetchJson;
const BASE_URL = window.nobid_endpoint + '/do_cost_report';
const BASE_URL_SERVICES = window.nobid_endpoint + '/do_cost_report_services';

function convertArrayOfObjectsToCSV (array) {
  if (!array || array.length === 0) return null;
  const keys = Object.keys(array[0]);
  let result = keys.join(',') + '\n';
  array.forEach(item => {
    let ctr = 0;
    keys.forEach(key => {
      if (ctr > 0) result += ',';
      result += item[key];
      ctr++;
    });
    result += '\n';
  });
  return result;
}

function downloadCSV (array) {
  const link = document.createElement('a');
  let csv = convertArrayOfObjectsToCSV(array);
  if (csv == null) return;
  if (!csv.match(/^data:text\/csv/i)) {
    csv = `data:text/csv;charset=utf-8,${csv}`;
  }
  link.setAttribute('href', encodeURI(csv));
  link.setAttribute('download', 'cloud_costs_export.csv');
  link.click();
}

const choicesInterval = [
  { id: 'day', name: 'Daily' },
  { id: 'week', name: 'Weekly' },
  { id: 'month', name: 'Monthly' }
];

const dateRangeChoices = [
  { id: 1, name: 'Yesterday' },
  { id: 7, name: 'Last 7 Days' },
  { id: 8, name: 'Last 8 Days' },
  { id: 15, name: 'Last 15 Days' },
  { id: 30, name: 'Last 30 Days' },
  { id: 'lm', name: 'Last Month' },
  { id: 'mtd', name: 'Month to Date' },
  { id: 'custom', name: 'Custom' }
];

const chartMetricsChoices = [
  { id: 'daily_cost', name: '$Daily Cost' },
  { id: 'daily_cost_with_discount', name: '$Daily Cost (w/ Discount)' },
  { id: 'daily_discount', name: '$Daily Discount' },
  { id: 'daily_invoice_total', name: '$Daily Invoice Total' },
  { id: 'month_to_date_cost', name: '$MTD Cost' },
  { id: 'month_to_date_cost_with_discount', name: '$MTD Cost (w/ Discount)' }
];

const serviceChartMetricsChoices = [
  { id: 'daily_cost', name: '$Daily Cost' },
  { id: 'daily_cost_with_discount', name: '$Daily Cost (w/ Discount)' },
  { id: 'mtd_cost', name: '$MTD Cost' },
  { id: 'mtd_cost_with_discount', name: '$MTD Cost (w/ Discount)' }
];

const chartFormatChoices = [
  { id: 'line', name: 'Lines' },
  { id: 'spline', name: 'Smoothed' },
  { id: 'column', name: 'Bars' },
  { id: 'area', name: 'Area' }
];

const fmt = (val) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(val) || 0);
const fmtCurrency = (val) => '$' + fmt(val);

const providerChoices = [
  { id: 'all', name: 'All Providers' },
  { id: 'digitalocean', name: 'DigitalOcean' },
  { id: 'aws', name: 'AWS' }
];

const dailyCostColumns = [
  { name: 'Date', selector: 'date', sortable: true, cell: row => row.date },
  { name: 'Provider', selector: 'provider', sortable: true, cell: row => <span>{row.provider}</span> },
  { name: '$Daily Cost', selector: 'daily_cost', sortable: true, cell: row => <span>{fmtCurrency(row.daily_cost)}</span> },
  { name: '$Daily Discount', selector: 'daily_discount', sortable: true, cell: row => <span>{fmtCurrency(row.daily_discount)}</span> },
  { name: '$Daily Cost (Discounted)', selector: 'daily_cost_with_discount', sortable: true, cell: row => <span>{fmtCurrency(row.daily_cost_with_discount)}</span> },
  { name: '$Daily Credits', selector: 'daily_credits', sortable: true, cell: row => <span>{fmtCurrency(row.daily_credits)}</span> },
  { name: '$Daily Invoice', selector: 'daily_invoice_total', sortable: true, cell: row => <span>{fmtCurrency(row.daily_invoice_total)}</span> },
];

const serviceCostColumns = [
  { name: 'Date', selector: 'date', sortable: true, cell: row => row.date },
  { name: 'Provider', selector: 'provider', sortable: true, cell: row => <span>{row.provider}</span> },
  { name: 'Service', selector: 'service', sortable: true, cell: row => <span>{row.service}</span> },
  { name: '$Daily Cost', selector: 'daily_cost', sortable: true, cell: row => <span>{fmtCurrency(row.daily_cost)}</span> },
  { name: '$Daily Cost (Discounted)', selector: 'daily_cost_with_discount', sortable: true, cell: row => <span>{fmtCurrency(row.daily_cost_with_discount)}</span> },
];

class DOCostReportComponent extends React.Component {
  state = {
    dailyData: [],
    serviceData: [],
    loading: true,
    interval: 'day',
    date_range: 30,
    start_date: '',
    end_date: '',
    dailyMetric: 'daily_cost',
    dailyFormat: 'spline',
    dailyProvider: 'all',
    serviceMetric: 'daily_cost_with_discount',
    serviceFormat: 'column',
    serviceProvider: 'all',
    selectedServices: [],
    provider: 'all'
  };

  componentDidMount () {
    this.fetchData();
  }

  buildQueryString () {
    const { interval, date_range, start_date, end_date, provider } = this.state;
    let qs = `interval=${interval}`;
    if (date_range === 'custom') {
      if (start_date) qs += `&start_date=${start_date}`;
      if (end_date) qs += `&end_date=${end_date}`;
      qs += '&date_range=custom';
    } else {
      qs += `&date_range=${date_range}`;
    }
    if (provider && provider !== 'all') {
      qs += `&provider=${provider}`;
    }
    return qs;
  }

  fetchData () {
    this.setState({ loading: true });
    const qs = this.buildQueryString();
    const options = { headers: new Headers() };
    options.headers.set('accept', 'application/json');
    const token = localStorage.getItem('token');
    options.headers.set('Authorization', `Bearer ${token}`);

    Promise.all([
      fetch(`${BASE_URL}?${qs}`, options),
      fetch(`${BASE_URL_SERVICES}?${qs}`, options)
    ]).then(([dailyRes, serviceRes]) => {
      this.setState({
        dailyData: dailyRes.json || [],
        serviceData: serviceRes.json || [],
        loading: false
      }, () => {
        this.updateDailyChart();
        this.updateServiceChart();
      });
    }).catch(err => {
      console.error('DO Cost Report fetch error:', err);
      this.setState({ loading: false });
    });
  }

  handleRefreshReport = () => {
    this.fetchData();
  }

  getFilteredDailyData () {
    const { dailyData, dailyProvider } = this.state;
    if (!dailyProvider || dailyProvider === 'all') return dailyData;
    return dailyData.filter(row => row.provider === dailyProvider);
  }

  updateDailyChart (overrides) {
    if (this.refs.dailyChart) {
      const metric = (overrides && overrides.metric) || this.state.dailyMetric;
      const format = (overrides && overrides.format) || this.state.dailyFormat;
      const filtered = this.getFilteredDailyData();
      // Map provider into publisher_name so chart creates one series per provider
      const chartData = filtered.map(row => ({
        ...row,
        publisher_name: row.provider
      }));
      this.refs.dailyChart.clearChart();
      this.refs.dailyChart.refreshChart(chartData, chartMetricsChoices, chartFormatChoices, { metric, format });
    }
  }

  getFilteredServiceData () {
    const { serviceData, selectedServices, serviceProvider } = this.state;
    let data = serviceData;
    if (serviceProvider && serviceProvider !== 'all') {
      data = data.filter(row => row.provider === serviceProvider);
    }
    if (selectedServices.length === 0) return data;
    return data.filter(row => selectedServices.includes(row.service));
  }

  updateServiceChart (overrides) {
    if (this.refs.serviceChart && this.state.serviceData.length > 0) {
      const metric = (overrides && overrides.metric) || this.state.serviceMetric;
      const format = (overrides && overrides.format) || this.state.serviceFormat;
      const filtered = this.getFilteredServiceData();
      // Clear old series before refreshing with filtered data
      this.refs.serviceChart.clearChart();
      // Map provider:service into publisher_name so the chart creates one series per provider+service
      const chartData = filtered.map(row => ({
        ...row,
        publisher_name: row.provider ? `${row.provider}:${row.service}` : row.service
      }));
      this.refs.serviceChart.refreshChart(chartData, serviceChartMetricsChoices, chartFormatChoices, { metric, format });
    }
  }

  handleDailyChartMetricChanged = (value) => {
    const metric = value.target.value;
    this.setState({ dailyMetric: metric }, () => this.updateDailyChart());
  }

  handleDailyChartFormatChanged = (value) => {
    const format = value.target.value;
    this.setState({ dailyFormat: format }, () => this.updateDailyChart());
  }

  handleServiceChartMetricChanged = (value) => {
    const metric = value.target.value;
    this.setState({ serviceMetric: metric }, () => this.updateServiceChart());
  }

  handleServiceChartFormatChanged = (value) => {
    const format = value.target.value;
    this.setState({ serviceFormat: format }, () => this.updateServiceChart());
  }

  handleServiceFilterChanged = (e) => {
    const selected = Array.from(e.target.selectedOptions, opt => opt.value);
    this.setState({ selectedServices: selected }, () => this.updateServiceChart());
  }

  render () {
    const { loading, dailyData, serviceData } = this.state;
    const filteredServiceData = this.getFilteredServiceData();
    const serviceProvider = this.state.serviceProvider;
    const filteredByProvider = (!serviceProvider || serviceProvider === 'all') ? serviceData : serviceData.filter(r => r.provider === serviceProvider);
    const serviceNames = [...new Set(filteredByProvider.map(r => r.service))].sort();

    return (
      <div id="do-cost-report-wrapper">
        <Create basePath="/do_cost_report" resource="" title="Tech Costs" {...this.props}>
          <SimpleForm toolbar={null}>

            <div style={{ width: '100%', overflow: 'hidden' }}>
              <div style={{ float: 'left', paddingRight: '5px' }}>
                <SelectInput label="Interval" source="interval" choices={choicesInterval}
                  format={() => this.state.interval}
                  onChange={value => this.setState({ interval: value.target.value })}
                  disabled={loading} />
              </div>

              <div style={{ float: 'left', paddingRight: '5px' }}>
                <SelectInput label="Date Range" source="date_range" choices={dateRangeChoices}
                  format={() => this.state.date_range}
                  onChange={value => this.setState({ date_range: value.target.value })}
                  disabled={loading} />

                <div style={{ float: 'right', paddingLeft: '5px', display: this.state.date_range !== 'custom' ? 'none' : 'block' }}>
                  <div style={{ float: 'left', paddingRight: '5px' }}>
                    <DateInput label="Start Date" source="start_date" allowEmpty
                      format={() => this.state.start_date}
                      onChange={value => this.setState({ start_date: value.target.value })}
                      disabled={loading} />
                  </div>
                  <div style={{ float: 'left' }}>
                    <DateInput label="End Date" source="end_date" allowEmpty
                      format={() => this.state.end_date}
                      onChange={value => this.setState({ end_date: value.target.value })}
                      disabled={loading} />
                  </div>
                </div>
              </div>

              <div style={{ float: 'left', paddingRight: '5px' }}>
                <SelectInput label="Provider" source="provider" choices={providerChoices}
                  format={() => this.state.provider}
                  onChange={value => this.setState({ provider: value.target.value })}
                  disabled={loading} />
              </div>

              <div style={{ float: 'right', paddingTop: '25px', paddingLeft: '10px' }}>
                <Button label="Run Report" onClick={this.handleRefreshReport} variant="contained"
                  color="primary" startIcon={<RunReportIcon />} disabled={loading} />
              </div>
            </div>

          </SimpleForm>
        </Create>

        {/* Daily Costs Chart */}
        <div style={{ marginTop: '10px', overflow: 'hidden' }}>
          <h3 style={{ color: '#ccc', marginBottom: '5px', float: 'left' }}>Daily Costs</h3>
          <div style={{ float: 'right', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <select defaultValue="spline" onChange={e => this.handleDailyChartFormatChanged({ target: { value: e.target.value } })}
              style={{ padding: '5px', background: '#333', color: '#ccc', border: '1px solid #555' }}>
              {chartFormatChoices.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select defaultValue="daily_cost" onChange={e => this.handleDailyChartMetricChanged({ target: { value: e.target.value } })}
              style={{ padding: '5px', background: '#333', color: '#ccc', border: '1px solid #555' }}>
              {chartMetricsChoices.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        <NoBidChartComponent ref="dailyChart" legend={{ enabled: false }} />

        <div style={{ height: '5px' }} />

        <DataTable
          title="Daily Cost Details"
          theme={window.nobid_table_theme()}
          columns={dailyCostColumns}
          data={dailyData}
          progressPending={loading}
          pagination
          fixedHeader
          paginationPerPage={30}
          dense
          highlightOnHover
          subHeader={!loading}
          subHeaderComponent={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <CloudDownload label="Export" style={{ margin: '5px', cursor: 'pointer' }} color="action"
                onClick={() => downloadCSV(dailyData)} />
            </div>
          }
        />

        {/* Service Costs Chart */}
        <div style={{ marginTop: '30px', overflow: 'hidden' }}>
          <h3 style={{ color: '#ccc', marginBottom: '5px', float: 'left' }}>Cost Per Service</h3>
          <div style={{ float: 'right', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <select multiple size={Math.min(serviceNames.length, 6)} onChange={this.handleServiceFilterChanged}
              style={{ padding: '5px', background: '#333', color: '#ccc', border: '1px solid #555', minWidth: '150px' }}>
              {serviceNames.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select defaultValue="column" onChange={e => this.handleServiceChartFormatChanged({ target: { value: e.target.value } })}
              style={{ padding: '5px', background: '#333', color: '#ccc', border: '1px solid #555' }}>
              {chartFormatChoices.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select defaultValue="daily_cost_with_discount" onChange={e => this.handleServiceChartMetricChanged({ target: { value: e.target.value } })}
              style={{ padding: '5px', background: '#333', color: '#ccc', border: '1px solid #555' }}>
              {serviceChartMetricsChoices.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        <NoBidChartComponent ref="serviceChart" legend={{ enabled: false }}
          plotOptions={{
            column: { stacking: 'normal', borderWidth: 0 },
            series: {
              turboThreshold: 5000,
              events: {
                legendItemClick: function (e) {
                  e.preventDefault();
                  const chart = this.chart;
                  const clicked = this;
                  const allVisible = chart.series.every(s => s.visible);
                  if (allVisible) {
                    // Isolate: hide all others, show only clicked
                    chart.series.forEach(s => { if (s !== clicked) s.hide(); });
                  } else if (clicked.visible && chart.series.filter(s => s.visible).length === 1) {
                    // Only this one visible, show all
                    chart.series.forEach(s => s.show());
                  } else {
                    // Toggle the clicked one
                    if (clicked.visible) clicked.hide(); else clicked.show();
                  }
                }
              }
            }
          }}
        />

        <div style={{ height: '5px' }} />

        <DataTable
          title="Service Cost Details"
          theme={window.nobid_table_theme()}
          columns={serviceCostColumns}
          data={filteredServiceData}
          progressPending={loading}
          pagination
          fixedHeader
          paginationPerPage={30}
          dense
          highlightOnHover
          subHeader={!loading}
          subHeaderComponent={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <CloudDownload label="Export" style={{ margin: '5px', cursor: 'pointer' }} color="action"
                onClick={() => downloadCSV(filteredServiceData)} />
            </div>
          }
        />

      </div>
    );
  }
}

export const DOCostReportList = (props) => (
  <DOCostReportComponent {...props} />
);
